import React, { useState, useRef, useEffect } from 'react';
import './ChatAi.css';

const API_BASE_URL = 'https://hub2.daolog.net';


const SendIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="2"/>
  </svg>
);

const UserIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="8" r="4"/>
    <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7"/>
  </svg>
);

const BotIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10"/>
    <path d="M8 10h2M14 10h2M8 14h8" stroke="white" strokeWidth="2"/>
  </svg>
);

const PlusIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M12 5v14M5 12h14" strokeWidth="2"/>
  </svg>
);

const FilePlusIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6M12 18v-6M9 15h6"/>
  </svg>
);

const MessageIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const ThumbsUpIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
  </svg>
);

const ThumbsDownIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/>
  </svg>
);

const MehIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="10"/>
    <path d="M8 15h8M9 9h.01M15 9h.01"/>
  </svg>
);

function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );
}

function SubmitButton({ isPending }) {
  return (
    <button 
      type="submit" 
      disabled={isPending}
      className="send-button"
      aria-label="Відправити повідомлення"
    >
      {isPending ? (
        <div className="loading-spinner"></div>
      ) : (
        <SendIcon />
      )}
    </button>
  );
}

function FeedbackIcons({ onOpenFeedback, responseTime, content, currentUser }) {
  const [voteSent, setVoteSent] = useState(null);
  const [isDislikePopoverOpen, setDislikePopoverOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVote = async (vote, reasonCode, reasonLabel) => {
    if (voteSent !== null) {
      return;
    }

    if (!content || !content.source || !content.case) {
      alert('Недостатньо даних для відправки відгуку.');
      return;
    }
    
    if (!currentUser) {
      alert('Необхідно авторизуватися.');
      return;
    }
    
    setIsSubmitting(true);
    
    const tableMap = {
      site: 'site',
      bz: 'knowledge',
      telegram: 'telegram',
      telegrambad: 'telegrambad'
    };

    const tableName = tableMap[(content.source || '').toLowerCase()];
    if (!tableName) {
      alert('Невідоме джерело для відгуку.');
      setIsSubmitting(false);
      setDislikePopoverOpen(false);
      return;
    }

    const payload = {
      targetTable: tableName,
      targetId: content.case,
      userId: currentUser?.userId || 'guest',
      vote: vote
    };

    if (reasonCode && reasonLabel) {
      payload.reasonCode = reasonCode;
      payload.reasonLabel = reasonLabel;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setVoteSent(vote);
        if (vote === -1) {
          alert(`Відгук збережено: ${reasonLabel}`);
        } else {
          alert('Дякуємо за ваш відгук!');
        }
      } else {
        const errorData = await response.json();
        alert(`Не вдалося відправити відгук: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Помилка відправки відгуку:', error);
      alert('Помилка відправки відгуку');
    } finally {
      setIsSubmitting(false);
      setDislikePopoverOpen(false);
    }
  };

  const handleDislikeReasonClick = (reason) => {
    if (isSubmitting) return;
    handleVote(-1, reason.code, reason.label);
  };

  const formatResponseTime = (time) => {
    if (!time) return '';
    const seconds = Math.floor((time / 1000) % 60);
    const minutes = Math.floor((time / (1000 * 60)) % 60);
    return minutes > 0 ? `${minutes}м ${seconds}с` : `${seconds}с`;
  };

  return (
    <div className="feedback-section">
      <div className="feedback-buttons">
        <button
          className={`feedback-btn ${voteSent === 1 ? 'active-like' : ''}`}
          onClick={() => handleVote(1)}
          disabled={voteSent !== null || isSubmitting}
          title="Допомогло"
        >
          <ThumbsUpIcon />
        </button>
        <button
          className={`feedback-btn ${voteSent === -0.5 ? 'active-meh' : ''}`}
          onClick={() => handleVote(-0.5)}
          disabled={voteSent !== null || isSubmitting}
          title="Норм"
        >
          <MehIcon />
        </button>
        <div className="dislike-dropdown">
          <button
            className={`feedback-btn ${voteSent === -1 ? 'active-dislike' : ''}`}
            disabled={voteSent !== null || isSubmitting}
            title="Не допомогло"
            onClick={() => setDislikePopoverOpen(!isDislikePopoverOpen)}
          >
            <ThumbsDownIcon />
          </button>
          {isDislikePopoverOpen && (
            <div className="dislike-reasons-popover">
              <div className="dislike-reasons-grid">
                {DISLIKE_REASONS.map((reason) => (
                  <button
                    key={reason.code}
                    className="dislike-reason-btn"
                    onClick={() => handleDislikeReasonClick(reason)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <div className="small-spinner"></div> : reason.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          className="feedback-btn"
          onClick={onOpenFeedback}
          title="Детальний відгук"
        >
          <MessageIcon />
        </button>
      </div>
      {responseTime && <span className="response-time">{formatResponseTime(responseTime)}</span>}
    </div>
  );
}

function BotMessage({ content, typing, onOpenFeedback, responseTime, currentUser }) {
  if (typing) {
    return <TypingIndicator />;
  }

  let displayContent;
  let actualContent = content;

  if (typeof content === 'string') {
    displayContent = content;
    actualContent = null;
  } else if (content && typeof content === 'object') {
    let textObject = content.text;
    if (typeof textObject === 'string') {
      try {
        const parsed = JSON.parse(textObject);
        if (parsed && typeof parsed === 'object') {
          textObject = parsed;
        }
      } catch (e) {
      }
    }

    if (textObject && typeof textObject === 'object' && textObject.text) {
      actualContent = textObject;
      displayContent = actualContent.text;
    } else if (typeof textObject === 'string') {
      actualContent = content;
      displayContent = textObject;
    } else if (typeof content.output === 'string') {
      actualContent = content;
      displayContent = content.output;
    } else {
      displayContent = <pre className="json-content"><code>{JSON.stringify(content, null, 2)}</code></pre>;
      actualContent = null;
    }
  } else {
    displayContent = "Повідомлення не розпізнано.";
    actualContent = null;
  }
  
  const feedbackBlacklist = [
    "Відправляємо на перевірку",
    "Помилка при відповіді ШІ"
  ];
  
  let showFeedback = true;
  const contentString = typeof displayContent === 'string' ? displayContent : '';

  if (!actualContent || !contentString.trim() || (feedbackBlacklist.some(phrase => contentString.includes(phrase)))) {
    showFeedback = false;
  }

  return (
    <div>
      <div className="message-text">{displayContent}</div>
      {showFeedback && (
        <FeedbackIcons 
          onOpenFeedback={onOpenFeedback} 
          responseTime={responseTime} 
          content={actualContent} 
          currentUser={currentUser} 
        />
      )}
    </div>
  );
}

function ChatPage() {
  const [selectedSources, setSelectedSources] = useState({
    site: true,
    bz: true,
    telegram: false
  });
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const sources = [
    { key: 'site', label: 'Сайт' },
    { key: 'bz', label: 'БЗ' },
    { key: 'telegram', label: 'Телеграм' }
  ];

  useEffect(() => {
    scrollToBottom();
    initializeUser();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleSource = (sourceKey) => {
    setSelectedSources(prev => ({
      ...prev,
      [sourceKey]: !prev[sourceKey]
    }));
  };

  const initializeUser = async () => {
    if (!currentUser) {
      await fetchCurrentUser();
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      } else {
        console.warn('Не вдалося отримати користувача, використовуємо гостейовий режим');
        setCurrentUser({
          userId: `guest-${Date.now()}`,
          isGuest: true
        });
      }
    } catch (error) {
      console.error('Помилка отримання користувача:', error);
      setCurrentUser({
        userId: `guest-${Date.now()}`,
        isGuest: true
      });
    }
  };

  const incrementReceivedCount = async (targetTable, caseId) => {
    if (!caseId) return;
    
    try {
      await fetch(`${API_BASE_URL}/api/feedback/increment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetTable: targetTable,
          id: caseId
        })
      });
    } catch (error) {
      console.error('Помилка інкрементації лічильника:', error);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
  };

  const sendMessage = async (feedback) => {
    if (isLoading) return;

    if (!currentUser) {
      await fetchCurrentUser();
    }

    const userMessageContent = feedback 
      ? `Відгук: ${feedback.summary}`
      : message;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date()
    };

    const typingMessage = {
      id: Date.now() + 1,
      role: 'bot',
      content: '',
      typing: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, typingMessage]);
    if (!feedback) setMessage('');
    setIsLoading(true);

    const startTime = Date.now();

    try {
      const requestData = feedback
        ? {
            review: true,
            review_message: feedback.summary,
            cases: JSON.stringify([feedback.case]),
            sessionId: currentUser?.userId || `guest-${Date.now()}`
          }
        : {
            message: userMessageContent,
            sessionId: currentUser?.userId || `guest-${Date.now()}`,
            review: false,
            site: selectedSources.site,
            bz: selectedSources.bz,
            telegram: selectedSources.telegram
          };

      const response = await fetch(`${API_BASE_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Помилка авторизації. Перевірте доступ.');
        }
        throw new Error(`HTTP помилка! статус: ${response.status}`);
      }

      const data = await response.json();
      const responseTime = Date.now() - startTime;
      
      let botResponse;
      if (typeof data.response === 'string') {
        try {
          const parsedResponse = JSON.parse(data.response);
          botResponse = Array.isArray(parsedResponse) ? parsedResponse[0] : parsedResponse;
        } catch {
          botResponse = { text: data.response };
        }
      } else {
        botResponse = Array.isArray(data.response) ? data.response[0] : data.response;
      }

      const botMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: botResponse,
        timestamp: new Date(),
        responseTime: responseTime
      };

      setMessages(prev => [...prev.filter(m => !m.typing), botMessage]);

      if (botResponse.case) {
        const tableMap = {
          site: 'site',
          bz: 'knowledge', 
          telegram: 'telegram',
          telegrambad: 'telegrambad'
        };
        
        const targetTable = tableMap[botResponse.source?.toLowerCase()] || 'knowledge';
        await incrementReceivedCount(targetTable, botResponse.case);
      }

    } catch (error) {
      console.error('Помилка відправки повідомлення:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: { text: 'Помилка при отриманні відповіді: ' + error.message },
        timestamp: new Date()
      };
      setMessages(prev => [...prev.filter(m => !m.typing), errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  const submitFeedback = (feedbackData) => {
    closeFeedbackModal();
    sendMessage(feedbackData);
  };

  const renderMessage = (msg) => {
    return (
      <div key={msg.id} className={`message-row ${msg.role}`}>
        <div className="message-content">
          {msg.role === 'bot' ? (
            <BotMessage 
              content={msg.content} 
              typing={msg.typing}
              onOpenFeedback={openFeedbackModal}
              responseTime={msg.responseTime}
              currentUser={currentUser}
            />
          ) : (
            <div className="message-text">{msg.content}</div>
          )}
        </div>
        
        <div className="user-icon">
          {msg.role === 'user' ? (
            <UserIcon />
          ) : (
            <BotIcon />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-page">
      {isFeedbackModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Створити відгук</h3>
            <p>Функціонал детального відгуку буде реалізовано тут</p>
            <div className="modal-actions">
              <button onClick={closeFeedbackModal}>Скасувати</button>
              <button onClick={() => submitFeedback({ summary: 'Тестовий відгук', case: { id: 'test', source: 'site' } })}>
                Відправити
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-container">
        <div className="chat-header">
          <div className="header-left">
            <button
              className="new-chat-btn"
              onClick={handleNewChat}
              disabled={!currentUser || messages.length === 0}
            >
              <FilePlusIcon />
              <span>Новий чат</span>
            </button>
          </div>
          <h2>ШІ Ментор</h2>
          <div className="header-right">
            {currentUser?.isGuest && (
              <div className="guest-badge">Гостейовий режим</div>
            )}
          </div>
        </div>

        <div className="chat-messages">
          {!currentUser ? (
            <div className="empty-state">
              <BotIcon />
              <p>Будь ласка, увійдіть, щоб почати чат.</p>
            </div>
          ) : (
            <>
              {messages.length === 0 && (
                <div className="empty-state">
                  <BotIcon />
                  <p>Відправте ваше перше повідомлення.</p>
                </div>
              )}
              {messages.map(msg => renderMessage(msg))}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <div className="source-selector">
            <span className="source-label">Джерело:</span>
            <div className="source-buttons">
              {sources.map(source => (
                <button
                  key={source.key}
                  className={`source-button ${selectedSources[source.key] ? 'active' : ''}`}
                  onClick={() => toggleSource(source.key)}
                >
                  {source.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="input-row">
            <button
              type="button"
              className="add-button"
              onClick={openFeedbackModal}
              disabled={!currentUser}
            >
              <PlusIcon />
            </button>
            <input
              type="text"
              className="message-input"
              placeholder={!currentUser ? "Будь ласка, спочатку увійдіть" : "Запитайте щось..."}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !currentUser}
            />
            <SubmitButton isPending={isLoading} />
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;